import MatchingChip from 'components/Chips/MatchingChip';
import MentorshipStatusChip from 'components/Chips/MentorshipStatusChip';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import getFilters from 'components/Filters';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import SettingsContainer from 'components/list/SettingsContainer';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import { getColumnNumber } from 'components/list/utils';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  setActiveColumns,
  setOrder,
  setPage,
  setTake,
} from 'store/actions/admin/menteeLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Mentorship from 'types/Mentorship';
import { Action } from 'types/typeguards/reducerActions';
import { getLocaleDate } from 'utils/time';
import { MenteeEditContext } from '..';

const Filters = getFilters<never>();

export const columnNames = [
  'matching',
  'firstName',
  'lastName',
  'organism',
  'start',
  'end',
  'status',
] as const;
export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};

export const sortableColumns = [
  'matching',
  'firstName',
  'lastName',
  'organism',
  'start',
  'end',
  'status',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export const initialActiveColumns: Columns = {
  matching: true,
  firstName: true,
  lastName: true,
  organism: true,
  start: true,
  end: true,
  status: true,
};

const useCustomSWR = (
  id: number | undefined,
  take: number,
  page: number,
  order: OrderValue,
) => {
  const { data, error } = useSWR(
    `/mentorship/mentee/${id}/history/?take=${take}&page=${page}&order=${order}`,
  );

  if (!id) {
    return { data: undefined };
  }
  return { data, error };
};
type RetrievedData = {
  mentorships: Mentorship[] | undefined;
  pages: number | undefined;
  count: number | undefined;
};
type Props = {
  page: number;
  setPage: (page: number) => void;
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};
const History: FC<Props> = ({
  page,
  setPage,
  activeColumns,
  setActiveColumns,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { mentee } = useContext(MenteeEditContext);
  const { data, error }: { data?: RetrievedData; error?: unknown } =
    useCustomSWR(mentee?.id, take, page, order);
  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentorships: undefined,
    pages: undefined,
    count: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    return getColumnNumber(activeColumns, false);
  }, [activeColumns]);

  return error ? (
    <p>L'utilisateur n'a pas rempli son formulaire correctement.</p>
  ) : retrievedData.mentorships ? (
    <>
      <Filters
        tagList={[]}
        setTags={() => null}
        state={{ tags: [], search: '', take }}
        setTake={setTake}
      />
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentors"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Matching"
            checked={selectedColumns.matching}
            name="matching"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Organisme"
            checked={selectedColumns.organism}
            name="organism"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date de début"
            checked={selectedColumns.start}
            name="start"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date de fin"
            checked={selectedColumns.end}
            name="end"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Statut du mentorat"
            checked={selectedColumns.status}
            name="status"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>
      <List
        activeColumns={activeColumns}
        columnNumber={columnNumber}
        isEmpty={retrievedData.mentorships?.length === 0}
        emptyText="L'historique de ce mentoré est vide"
        pages={retrievedData.pages}
        page={page}
        setPage={setPage}
        loading={!data}
      >
        <OrderTh name="matching" order={order} setOrder={setOrder}>
          Matching
        </OrderTh>
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <OrderTh name="organism" order={order} setOrder={setOrder}>
          Organisme
        </OrderTh>
        <OrderTh name="start" order={order} setOrder={setOrder}>
          Date début
        </OrderTh>
        <OrderTh name="end" order={order} setOrder={setOrder}>
          Date de fin
        </OrderTh>
        <OrderTh name="status" order={order} setOrder={setOrder}>
          Statut du mentorat
        </OrderTh>
        <Th name="settings">
          <SettingsContainer onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </SettingsContainer>{' '}
        </Th>
        {retrievedData.mentorships?.map((mentorship, idx) => (
          <React.Fragment key={idx}>
            <Td name="matching">
              {mentorship.mentor?.MentorMatching &&
                mentorship.mentor.MentorMatching[0] && (
                  <MatchingChip
                    value={mentorship.mentor.MentorMatching[0].value}
                  />
                )}
            </Td>
            <BoldTd name="firstName">
              <Link
                to={`/admin/mentoring/mentors/${
                  mentorship.mentor!.id
                }/mentoring/ongoing/`}
              >
                <EllipsisText text={mentorship.mentor!.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link
                to={`/admin/mentoring/mentors/${
                  mentorship.mentor!.id
                }/mentoring/ongoing/`}
              >
                <EllipsisText text={mentorship.mentor!.lastName || ''} />
              </Link>
            </BoldTd>
            <Td name="organism">
              <EllipsisText text={mentorship.mentor!.organism || ''} />
            </Td>
            <Td name="start">
              {mentorship.createdAt &&
                getLocaleDate(mentorship.createdAt || '')}
            </Td>
            <Td name="end">
              {mentorship.updatedAt &&
                getLocaleDate(mentorship.updatedAt || '')}
            </Td>
            <Td name="status">
              <MentorshipStatusChip status={mentorship.status!} />
            </Td>
            <Td name="settings" />
          </React.Fragment>
        ))}
      </List>
    </>
  ) : (
    <LoaderContainer>
      <DefaultLoader />
    </LoaderContainer>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.menteeLists.history.page,
    activeColumns: state.admin.menteeLists.history.activeColumns,
    take: state.admin.menteeLists.history.take,
    order: state.admin.menteeLists.history.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('history', page)),
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('history', activeColumns)),
    setTake: (take: number) => dispatch(setTake('history', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('history', order)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(History);
